import type { ReactNode } from 'react';

// The stacked-group contract, imported rather than re-declared.
//
// `TextInputPosition` belongs to the GROUP, not to `TextInput` — `FieldGroup` is what assigns
// it, and it assigns it to any child that accepts one. Re-declaring a structurally identical union
// here would let the two drift into disagreeing about what a position is while every type still
// checked, which is precisely the failure a group of mixed field types would surface at runtime.
// `TextArea/types.ts` and `Select/types.ts` take the same import for the same reason.
import type { FieldInfoProps } from '../InfoButton/types';
import type { TextInputPosition } from '../TextInput/types';

/** The two reading orders the field supports: day-first and month-first. */
export type DatePickerLocale = 'en-GB' | 'en-US';

export type DatePickerFormat = {
  /** What a person types, and what the field shows while it has focus. */
  typed: string;
  /** `typed`, spelled the way a person reads it. Shown only while the field is focused. */
  placeholder: string;
  /** How a committed date is shown once the field loses focus. */
  display: string;
  /** How the calendar button spells the committed date for a screen reader. */
  spoken: string;
  /** The calendar's first column: 1 is Monday, 0 is Sunday. */
  weekStartsOn: 0 | 1;
};

/**
 * The formats this component owns, one set per reading order.
 *
 * ── Why a committed date is spelled out ───────────────────────────────────────────────────────
 *
 * `05/03/2026` is 5 March to a British reader and 3 May to an American one, and this is the field
 * a host schedules an event with. So the typed form is only ever shown while someone is typing;
 * the moment the field loses focus it reads `Thu 5 Mar 2026`, which means one thing to everyone.
 * That also makes a mistyped order visible straight away: an American who types `05/03/2026`
 * meaning May sees March come back. The short weekday and month keep it narrow enough for a
 * half-width field; the house display format (`DEFAULT_DATE_FORMAT`) runs to about 30 characters.
 *
 * ── Why typing still has one order per locale ─────────────────────────────────────────────────
 *
 * `react-day-picker` ships no input, so typed entry is hand-rolled and must choose an order. The
 * locale decides it, and the placeholder shows it while focused (Figma `Date Picker` 1438:129727
 * brings `DD/MM/YYYY` in from `State=Active`). The default locale comes from the browser's time
 * zone — see `browserDatePickerLocale` — and a caller that knows better passes `locale`.
 *
 * The component itself stays free of `t()`, as every DS 2.0 component does, so these are
 * `date-fns` patterns rather than `Intl` output: English month names in both, and identical in
 * every browser, which is what lets the tests assert exact strings.
 */
export const DATE_PICKER_FORMATS: Record<DatePickerLocale, DatePickerFormat> = {
  'en-GB': {
    typed: 'dd/MM/yyyy',
    placeholder: 'DD/MM/YYYY',
    display: 'EEE d MMM yyyy',
    spoken: 'd MMMM yyyy',
    weekStartsOn: 1,
  },
  'en-US': {
    typed: 'MM/dd/yyyy',
    placeholder: 'MM/DD/YYYY',
    display: 'EEE, MMM d, yyyy',
    spoken: 'MMMM d, yyyy',
    weekStartsOn: 0,
  },
};

/**
 * The two names that exist only for assistive technology.
 *
 * Overridable, with English defaults: the
 * wording is the same on almost every screen, so a default earns its place, and a caller in
 * another language still has somewhere to put theirs. Partial at the prop, so replacing one does
 * not mean restating the other.
 *
 * They are functions rather than strings because neither is fixed text: the trigger's name has to
 * change with the committed value, and both interpolate the field's own `label`. A caller
 * translating them writes the whole sentence, including the date format, rather than being handed
 * fragments to reassemble — which is the part word order differs on between languages.
 *
 * The component itself stays free of `t()`, as every DS 2.0 component does: a design-system
 * primitive that reached for the app's i18n context could not be rendered outside it.
 */
export type DatePickerAccessibleLabels = {
  /**
   * The calendar button's accessible name, given the committed date (`null` when the field is
   * empty) and the field's `label`.
   */
  trigger: (value: Date | null, label: string) => string;
  /** The calendar popup's accessible name, given the field's `label`. */
  calendar: (label: string) => string;
};

/** Why a typed entry was refused. */
export type DatePickerEntryError = 'format' | 'notReal' | 'beforeMin' | 'afterMax';

/**
 * The sentences shown under the field when a typed entry is refused.
 *
 * Overridable with English defaults, for the reason `accessibleLabels` is: the component stays free
 * of `t()`, and a caller in another language still has somewhere to put its own wording. Functions
 * where the sentence carries a value, so a translator writes the whole sentence rather than
 * reassembling fragments. `limit` is the bound spelled out the way the field shows a date.
 */
export type DatePickerMessages = {
  /** `hint` is the typed format (`DD/MM/YYYY`), `example` a real date written in it (`05/03/2026`). */
  format: (hint: string, example: string) => ReactNode;
  notReal: ReactNode;
  beforeMin: (limit: string) => ReactNode;
  afterMax: (limit: string) => ReactNode;
};

export const DEFAULT_DATE_PICKER_MESSAGES: DatePickerMessages = {
  format: (hint, example) => `Use the format ${hint}, like ${example}`,
  notReal: "That date doesn't exist. Check the day and the month",
  beforeMin: (limit) => `Pick ${limit} or a later date`,
  afterMax: (limit) => `Pick ${limit} or an earlier date`,
};

export type DatePickerProps = FieldInfoProps & {
  // Required for the same reason as on `TextInput` and `TextArea`: this shape has no other visible
  // name, so a field without one is a nameless control the moment the user types.
  /** The floating label, and the field's only visible name. Required. */
  label: string;
  /**
   * The committed date, for a controlled field; pair with `onValueChange`. `null` is empty, while
   * `undefined` means uncontrolled.
   */
  value?: Date | null;
  /** The starting date for an uncontrolled field. */
  defaultValue?: Date | null;
  // Called from BOTH entry paths with the same value for the same day — a date chosen from the
  // grid and the same date typed into the field are indistinguishable here.
  //
  // An unparsable or out-of-range entry leaves the committed value untouched and puts the field in
  // its invalid state; see `DatePicker.tsx`. Blurring a field nobody typed into, or re-typing the
  // date that is already committed, reports nothing. Only a real change is a change.
  /**
   * Called with the committed date at local midnight, or `null` when cleared, from the grid or typed
   * entry. Not called for invalid or out-of-range entries, or when the day is unchanged.
   */
  onValueChange?: (value: Date | null) => void;
  /**
   * Called when the field starts refusing a typed entry (with the reason) and when it stops
   * (`null`), before any `onValueChange` the same edit causes, with the value the field holds once
   * it settles. A form that must not submit a refused entry can hold an invalid `Date` meanwhile:
   * passed back as `value`, the field reads one as "unchanged".
   */
  onEntryErrorChange?: (error: DatePickerEntryError | null, accepted: Date | null) => void;
  /**
   * Notified on every blur, before the typed entry is parsed.
   *
   * Separate from `onValueChange`, which reports a committed day and only when it actually moves.
   * A form library marking a field touched needs the blur itself, not the outcome of parsing it.
   */
  onBlur?: () => void;
  /** The earliest selectable day, inclusive. Days before it are disabled in the grid. */
  min?: Date;
  /** The latest selectable day, inclusive. Days after it are disabled in the grid. */
  max?: Date;
  /**
   * The reading order: `en-GB` types `dd/MM/yyyy` and starts weeks on Monday, `en-US` types
   * `MM/dd/yyyy` and starts on Sunday. Defaults to the browser's time zone — a US zone gets
   * `en-US`, everything else `en-GB`. A committed date is spelled out in either.
   */
  locale?: DatePickerLocale;
  /**
   * Translations for the sentences shown when a typed entry is refused, merged over the English
   * defaults. A refused entry shows its own sentence in place of `helperText` until it is fixed.
   */
  messages?: Partial<DatePickerMessages>;
  /** Sub-label text: the error message when invalid, the description otherwise. Announced either way. */
  helperText?: ReactNode;
  // The caller's error and the component's own parse failure are ORed: a field the caller has not
  // flagged still goes red on an unparsable entry, and clearing the caller's flag does not clear a
  // parse failure the user has not fixed.
  /**
   * Turns the field red and marks it `aria-invalid`. An unparsable entry is invalid regardless, and
   * clearing this does not clear it.
   */
  error?: boolean;
  disabled?: boolean;
  /**
   * Translations for the calendar button's and popup's screen-reader names, merged over the English
   * defaults. Never drawn; not the visible `label`.
   */
  accessibleLabels?: Partial<DatePickerAccessibleLabels>;
  /** Reaches the underlying `<input>`, so the field participates in an ordinary form submit. */
  name?: string;
  required?: boolean;
  /** Position in a stacked group. Leave unset inside `FieldGroup`, which assigns it; setting it overrides the group. */
  position?: TextInputPosition;
  /** Applied to the bordered box. */
  className?: string;
  /** Applied to the `Field.Root` wrapper rather than the box, for layout at the call site. */
  rootClassName?: string;
  /** Applied to the portalled calendar popup. */
  popupClassName?: string;
  /** Controlled open state for the calendar. Usually left alone. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Override which date the calendar considers "today". Used in stories to pin snapshots. */
  today?: Date;
  'data-testid'?: string;
};
